import React, {
  ChangeEvent,
  ClipboardEvent,
  FormEvent,
  KeyboardEvent,
  useRef,
} from "react";

interface GlobalTextInputFieldProps {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  title?: string;
  type?: string;
  fontSize?: string;
  fontWeight?: string;
  leadingElement?: React.ReactNode;
  trailingElement?: React.ReactNode;
  trailingElementOnClick?: () => void;
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  readonly?: boolean;
  borderColor?: string;
  is2FA?: boolean; // Prop to indicate if the input is for 2FA
  showSearchResults?: boolean;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  prefixIcon?: React.ReactNode;
  prefixIconPadding?: string;
  suffixIcon?: React.ReactNode; // New prop for the suffix icon
  suffixIconOnClick?: () => void; // Click handler for the suffix icon
  allowDecimal?: boolean;
  allowNegative?: boolean;
  full?: boolean;
  telegram?: boolean;
  inputBorder?: string;
  disabled?: boolean;
  showBorder?: boolean;
  backgroundColor?: string; // New prop for background color
  suffixIconPadding?: string;
}

const GlobalTextInputField: React.FC<GlobalTextInputFieldProps> = ({
  value,
  onChange,
  placeholder = "",
  className = "",
  title = "",
  type = "text",
  label,
  leadingElement,
  trailingElement,
  borderColor,
  fontSize = "10px",
  fontWeight = "font-normal",
  onKeyDown,
  trailingElementOnClick,
  onSubmit,
  readonly = false,
  is2FA = false,
  showSearchResults = false,
  onClick,
  prefixIcon,
  prefixIconPadding = "pl-[40px]",
  suffixIcon,
  suffixIconOnClick,
  allowDecimal = true,
  allowNegative = false,
  full = false,
  telegram = false,
  inputBorder,
  showBorder = true,
  backgroundColor = "bg-transparent", // Default to transparent
  suffixIconPadding = "pr-4",
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const numericPattern = allowDecimal
    ? allowNegative
      ? /^-?\d*(\.\d*)?$/
      : /^\d*(\.\d*)?$/
    : allowNegative
      ? /^-?\d*$/
      : /^\d*$/;

  // Handle keydown event for 2FA input (allow only numbers and limit to 6 digits)
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const { key } = e;
    const isAllowedKey = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "v",
    ].includes(key);

    if (type === "number") {
      // Prevent up/down arrow keys from incrementing/decrementing the value
      if (
        key === "ArrowUp" ||
        key === "ArrowDown" ||
        key === "e" ||
        key === "+"
      ) {
        e.preventDefault();
      }
      if (key === "-") {
        if (!allowNegative) {
          e.preventDefault();
        } else {
          const input = e.currentTarget;
          const start = input.selectionStart ?? 0;
          const end = input.selectionEnd ?? 0;
          const nextValue =
            input.value.slice(0, start) + "-" + input.value.slice(end);
          if (!numericPattern.test(nextValue)) {
            e.preventDefault();
          }
        }
      }
      // Prevent decimal points if allowDecimal is false
      if (!allowDecimal && key === ".") {
        e.preventDefault();
      }
    }

    if (is2FA) {
      const inputValue = (e.target as HTMLInputElement).value;
      if (inputValue.length >= 6 && !isAllowedKey) {
        e.preventDefault();
      }
      if (!key.match(/[0-9]/) && !isAllowedKey) {
        e.preventDefault();
      }
    }

    if (key === "Enter") {
      e.preventDefault();
    }

    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
    e.preventDefault();
    e.stopPropagation();
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;

    if (type === "number") {
      if (allowDecimal || allowNegative) {
        if (!numericPattern.test(inputValue)) {
          inputValue = value; // Revert to the last valid value
        }
      } else {
        // If decimals are not allowed, strip out all non-numeric characters including '.'
        inputValue = inputValue.replace(/[^0-9]/g, "");

        // Directly update the input element to prevent flickering
        if (inputRef.current) {
          inputRef.current.value = inputValue;
        }
      }
    }

    // Pass the sanitized value to the onChange handler
    onChange({
      ...e,
      target: {
        ...e.target,
        value: inputValue,
      },
    });
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    if (type === "number" || is2FA) {
      e.preventDefault();
      const pasteData = e.clipboardData.getData("Text");
      let filteredData = pasteData;

      if (type === "number") {
        const hasLeadingMinus =
          allowNegative && pasteData.trim().startsWith("-");
        // Filter to allow only numbers and decimal point if allowed
        filteredData = allowDecimal
          ? pasteData.replace(/[^0-9.]/g, "")
          : pasteData.replace(/[^0-9]/g, "");

        // Ensure only one decimal point
        if (allowDecimal) {
          const parts = filteredData.split(".");
          if (parts.length > 2) {
            filteredData = parts[0] + "." + parts.slice(1).join("");
          }
        }

        if (hasLeadingMinus) {
          filteredData = `-${filteredData}`;
        }
      } else if (is2FA) {
        filteredData = pasteData.replace(/[^0-9]/g, "").slice(0, 6);
      }

      if (inputRef.current) {
        const input = inputRef.current;
        const currentValue = input.value;
        const selectionStart = input.selectionStart || 0;
        const selectionEnd = input.selectionEnd || 0;
        const newValue =
          currentValue.substring(0, selectionStart) +
          filteredData +
          currentValue.substring(selectionEnd);

        const finalValue = is2FA ? newValue.slice(0, 6) : newValue;
        if (
          type === "number" &&
          (allowDecimal || allowNegative) &&
          !numericPattern.test(finalValue)
        ) {
          return;
        }
        input.value = finalValue;
        onChange({
          target: {
            value: finalValue,
          },
        } as ChangeEvent<HTMLInputElement>);
      }
    }
  };

  const borderClasses = showSearchResults
    ? "border-t-bg-button-primary border-l-bg-button-primary border-r-bg-button-primary border-b-border-subtle"
    : inputBorder
    ? inputBorder
    : "border-border-subtle rounded-b-lg";

  return (
    <div className={`flex flex-col ${className}`}>
      {title && (
        <>
          <span className="text-text-base text-[10px] font-semibold">
            {title}
          </span>
          <div className="my-[3px]" />
        </>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit?.(e);
        }}
      >
        {label && (
          <label
            htmlFor=""
            className="text-text-base text-[10px] font-semibold"
          >
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          {leadingElement && (
            <div className="absolute inset-y-0 left-0  flex items-center ">
              {typeof leadingElement === "string" ? (
                <img src={leadingElement} alt="" />
              ) : (
                leadingElement
              )}
            </div>
          )}
          {prefixIcon && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-[6px] h-full">
              {prefixIcon}
            </div>
          )}
          <input
            type={type === "number" && allowNegative ? "text" : type}
            placeholder={placeholder}
            className={`w-full px-2 py-[10px] text-text-base text-[${fontSize}] ${fontWeight} rounded-t-lg ${backgroundColor} ${
              showBorder ? `border ${borderClasses}` : ""
            } focus:outline-none placeholder:text-text-header ${
              leadingElement || prefixIcon ? `${prefixIconPadding}` : ""
            } ${suffixIcon ? "pr-[46px]" : ""} ${
              type === "number" ? "no-spinner" : ""
            }
            ${telegram ? "pl-[80px]" : ""}
            `}
            value={value}
            ref={inputRef}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onChange={handleInputChange}
            readOnly={readonly}
            onClick={onClick}
            onWheel={handleWheel}
            pattern={
              type === "number"
                ? allowNegative
                  ? allowDecimal
                    ? "^-?\\d*(\\.\\d{0,})?$"
                    : "^-?\\d*$"
                  : allowDecimal
                    ? "^\\d*(\\.\\d{0,})?$"
                    : undefined
                : undefined
            }
            inputMode={
              type === "number"
                ? allowDecimal || allowNegative
                  ? "decimal"
                  : "numeric"
                : undefined
            }
            style={{ outline: "none" }}
            {...props}
          />
          {trailingElement && (
            <span
              className="absolute inset-y-0 right-0 flex items-center pr-4 text-text-base font-semibold text-[12px] cursor-pointer"
              onClick={trailingElementOnClick}
            >
              {trailingElement}
            </span>
          )}
          {suffixIcon && (
            <span
              className={`absolute inset-y-0 right-0 flex items-center ${suffixIconPadding} cursor-pointer`}
              onClick={suffixIconOnClick}
            >
              {suffixIcon}
            </span>
          )}
        </div>
      </form>
    </div>
  );
};

export default GlobalTextInputField;
